import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/authStore";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Plus, LayoutDashboard, Settings, LogOut, CheckCircle2, AlertCircle, Clock, Search, Filter, ChevronLeft, ChevronRight, User, ChevronDown, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import artieLogo from '../assets/images/artie.png';
import { format } from "date-fns";
import { CampaignCard } from "@/components/brand-dashboard/CampaignCard";
import { Campaign } from "@/types/campaign";

export default function BrandDashboard() {
  const { user, brand, logout } = useAuthStore();
  // Pagination & Filtering State
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const ITEMS_PER_PAGE = 6;

  const brandId = brand?.id || brand?._id;

  const { data: campaigns = [], isLoading } = useQuery<Campaign[]>({
    queryKey: ["brandCampaigns", brandId],
    queryFn: () => apiClient.getBrandCampaigns(brandId!),
    enabled: !!brandId, // Only run the query if brandId is available
    staleTime: 5 * 60 * 1000, // Cache data for 5 minutes
  });

  const { data: brandPackages = [], isLoading: isLoadingPackages } = useQuery<any[]>({
    queryKey: ["brandPackages", brandId],
    queryFn: async () => {
      const response = await apiClient.getBrandPackages(brandId!, "active");
      return response.packages || [];
    },
    enabled: !!brandId, // Only run the query if brandId is available
    staleTime: 5 * 60 * 1000, // Cache data for 5 minutes
  });

  // Create a map of campaign_id to packages for easy lookup
  const packagesByCampaign = brandPackages.reduce((acc, pkg) => {
    if (pkg.campaign_id) {
      if (!acc[pkg.campaign_id]) {
        acc[pkg.campaign_id] = [];
      }
      acc[pkg.campaign_id].push(pkg);
    }
    return acc;
  }, {} as Record<string, any[]>);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isProfileDropdownOpen) {
        const target = event.target as HTMLElement;
        if (!target.closest('.profile-dropdown')) {
          setIsProfileDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileDropdownOpen]);

  // Filtering Logic - exclude cancelled campaigns by default
  const filteredCampaigns = campaigns.filter(campaign => {
    // Always filter out cancelled campaigns from the main dashboard view
    if (campaign.status === 'cancelled') {
      return false;
    }
    
    const matchesSearch = campaign.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination Logic
  const totalPages = Math.ceil(filteredCampaigns.length / ITEMS_PER_PAGE);
  const paginatedCampaigns = filteredCampaigns.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div className="min-h-screen bg-white font-geist">
      {/* Top Navigation */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left side - Logo */}
            <Link to="/brand-dashboard" className="flex items-center hover:opacity-80 transition-opacity">
              <img src={artieLogo} alt="Artie" className="w-10 h-10 mr-3" />
              <span className="font-work font-semibold text-3xl text-neutral-900 tracking-normal">artie</span>
            </Link>
            
            {/* Center - Navigation */}
            <nav className="hidden md:flex space-x-8">
              <Link to="/brand-dashboard" className="text-neutral-900 font-semibold">
                Home
              </Link>
              <Link to="/campaigns" className="text-neutral-600 hover:text-neutral-900 font-medium">
                Campaigns
              </Link>
              <span className="text-neutral-600 hover:text-neutral-900 font-medium cursor-pointer">IO & Payments</span>
              <span className="text-neutral-600 hover:text-neutral-900 font-medium cursor-pointer">Settings</span>
            </nav>
            
            {/* Right side - User info */}
            <div className="flex items-center space-x-4">
              <div className="relative profile-dropdown">
                <button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  className="flex items-center space-x-2 text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
                >
                  <span className="bg-neutral-100 px-3 py-1 rounded-full font-medium">
                    {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown Menu */}
                {isProfileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-neutral-200 rounded-lg shadow-lg z-50">
                    <div className="py-2">
                      <div className="px-4 py-2 border-b border-neutral-100">
                        <p className="text-sm font-medium text-neutral-900">{user?.name || 'User'}</p>
                        <p className="text-xs text-neutral-500">{user?.email || 'user@example.com'}</p>
                      </div>
                      <div className="pt-2">
                        <button
                          onClick={() => {
                            setIsProfileDropdownOpen(false);
                            logout();
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="w-4 h-4 mr-3" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Welcome Section */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">
              Welcome back, {brand?.name || (user as any)?.brand_profile?.parent_company || user?.name?.split(" ")[0]}
            </h1>
            <p className="text-neutral-500 mt-1">Manage your campaigns and view upcoming booking windows.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/campaigns/booking-windows">
              <Button className="bg-neutral-900 text-white hover:bg-neutral-800 hover:shadow-lg transition-all">
                <Plus className="w-4 h-4 mr-2" />
                Browse Booking Windows
              </Button>
            </Link>
          </div>
        </div>

        {/* Dashboard Grid */}
        <div className="grid gap-6 md:grid-cols-3 mb-8">
          <Card className="hover:border-neutral-400 transition-colors shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Campaigns</CardTitle>
              <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{campaigns.filter(c => c.status !== 'cancelled').length}</div>
              <p className="text-xs text-muted-foreground">Across all booking windows</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-yellow-500 hover:border-neutral-400 hover:border-l-yellow-500 transition-colors shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Budget</CardTitle>
              <span className="font-bold text-yellow-600">$</span>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${campaigns
                  .filter(c => c.status !== 'draft' && c.status !== 'cancelled')
                  .reduce((sum, c) => sum + (c.requirements?.budget_usd || 0), 0)
                  .toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">Committed to active campaigns</p>
            </CardContent>
          </Card>
          {(() => {
            // Filter out cancelled campaigns from all calculations
            const activeCampaigns = campaigns.filter(c => c.status !== 'cancelled');
            
            const draftCampaigns = activeCampaigns.filter(c => c.status === 'draft');
            const holdCampaigns = activeCampaigns.filter(c => c.status === 'hold');
            const inProgressCampaigns = activeCampaigns.filter(c => ['submitted', 'packaging', 'presented', 'active'].includes(c.status));
            
            // Find campaigns with ready packages (active packages available) that are not on hold or cancelled
            const campaignsWithReadyPackages = activeCampaigns.filter(campaign => {
              const campaignPackages = packagesByCampaign[campaign.id] || [];
              return campaign.status !== 'hold' && campaignPackages.length > 0 && campaignPackages.some(pkg => pkg.status === 'active');
            });
            
            let actionText, actionSubtext, actionLink, actionIcon;
            
            if (draftCampaigns.length > 0) {
              actionText = 'Complete Draft Campaign';
              actionSubtext = `You have ${draftCampaigns.length} unsaved draft${draftCampaigns.length > 1 ? 's' : ''}`;
              actionLink = `/campaigns/${draftCampaigns[0].id}`;
              actionIcon = <AlertCircle className="h-4 w-4 text-orange-600" />;
            } else if (holdCampaigns.length > 0) {
              actionText = 'View Hold Status';
              actionSubtext = `${holdCampaigns.length} campaign${holdCampaigns.length > 1 ? 's' : ''} with hold${holdCampaigns.length > 1 ? 's' : ''} placed`;
              actionLink = `/campaigns/${holdCampaigns[0].id}/hold-placed`;
              actionIcon = <Clock className="h-4 w-4 text-yellow-600" />;
            } else if (campaignsWithReadyPackages.length > 0) {
              // Prioritize campaigns with ready packages
              const campaign = campaignsWithReadyPackages[0];
              const packageCount = packagesByCampaign[campaign.id]?.filter(pkg => pkg.status === 'active').length || 0;
              actionText = 'Review Ready Package';
              actionSubtext = `${packageCount} package${packageCount > 1 ? 's' : ''} ready for ${campaign.name}`;
              actionLink = `/campaigns/${campaign.id}/packages`;
              actionIcon = <Package className="h-4 w-4 text-green-600" />;
            } else if (inProgressCampaigns.length > 0) {
              actionText = 'Review Active Campaigns';
              actionSubtext = `${inProgressCampaigns.length} campaign${inProgressCampaigns.length > 1 ? 's' : ''} in progress`;
              actionLink = '/campaigns';
              actionIcon = <Clock className="h-4 w-4 text-blue-600" />;
            } else {
              actionText = 'Create New Campaign';
              actionSubtext = 'Browse booking windows to get started';
              actionLink = '/campaigns/booking-windows';
              actionIcon = <Plus className="h-4 w-4 text-green-600" />;
            }
            
            return (
              <Link to={actionLink}>
                <Card className="border-l-4 border-l-neutral-900 hover:border-neutral-400 hover:border-l-neutral-900 transition-all shadow-sm hover:shadow-md cursor-pointer group">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium group-hover:text-neutral-700 transition-colors">Next Action</CardTitle>
                    {actionIcon}
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm font-medium group-hover:text-neutral-700 transition-colors">
                      {actionText}
                    </div>
                    <p className="text-xs text-muted-foreground group-hover:text-neutral-600 transition-colors">
                      {actionSubtext}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            );
          })()}
        </div>

        {/* Campaigns List */}
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h2 className="text-xl font-bold text-neutral-900">Your Campaigns</h2>

            {/* Search and Filter Controls */}
            {!isLoading && campaigns.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-neutral-500" />
                  <Input
                    type="text"
                    placeholder="Search campaigns..."
                    className="pl-9 bg-white border-neutral-200 focus:border-neutral-400 focus:ring-neutral-400"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="w-full sm:w-40">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full bg-white border-neutral-200 focus:ring-neutral-400">
                      <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-neutral-500" />
                        <SelectValue placeholder="Status" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="packaging">Packaging</SelectItem>
                      <SelectItem value="presented">Presented</SelectItem>
                      <SelectItem value="hold">Hold</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="complete">Complete</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="p-12 text-center text-neutral-500 bg-white rounded-xl shadow-sm border border-neutral-200">
              Loading campaigns...
            </div>
          ) : campaigns.length === 0 ? (
            <div className="p-12 text-center bg-white rounded-xl shadow-sm border border-neutral-200">
              <div className="w-12 h-12 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <LayoutDashboard className="w-6 h-6 text-neutral-400" />
              </div>
              <h3 className="text-neutral-900 font-medium mb-1">No campaigns yet</h3>
              <p className="text-neutral-500 text-sm mb-4">Create your first campaign to get started with Artie.</p>
              <Link to="/campaigns/booking-windows">
                <Button variant="outline" className="border-neutral-200 hover:bg-neutral-50 hover:text-neutral-900">Browse Booking Windows</Button>
              </Link>
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="p-12 text-center bg-white rounded-xl shadow-sm border border-neutral-200">
              <div className="w-12 h-12 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-6 h-6 text-neutral-400" />
              </div>
              <h3 className="text-neutral-900 font-medium mb-1">No campaigns found</h3>
              <p className="text-neutral-500 text-sm mb-4">Try adjusting your search or filters.</p>
              <Button
                variant="outline"
                onClick={() => { setSearchQuery(""); setStatusFilter("all"); }}
                className="border-neutral-200 hover:bg-neutral-50 hover:text-neutral-900"
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
                {paginatedCampaigns.map((campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    packages={packagesByCampaign[campaign.id] || []}
                  />
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between border-t border-neutral-200 pt-4 mt-6 gap-4">
                  <div className="text-sm text-neutral-500">
                    Showing <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to{" "}
                    <span className="font-medium">
                      {Math.min(currentPage * ITEMS_PER_PAGE, filteredCampaigns.length)}
                    </span>{" "}
                    of <span className="font-medium">{filteredCampaigns.length}</span> results
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="border-neutral-200 hover:bg-neutral-50 text-neutral-700"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <div className="flex items-center justify-center min-w-[80px] text-sm font-medium">
                      Page {currentPage} of {totalPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="border-neutral-200 hover:bg-neutral-50 text-neutral-700"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

      </main>
    </div>
  );
}
